/**
 * Desired Outcome Framework - Complete Enhanced Entry
 * Exemplar of Type 2 (Clarity & Alignment) framework depth
 * 
 * Core Question: "How will you know when you have what you want?"
 */

import type { EnhancedFrameworkEntry } from '../../types/enhancedFramework';

export const desiredOutcomeFramework: EnhancedFrameworkEntry = {
  // ===== BASIC IDENTITY =====
  id: 'desired-outcome',
  canonicalName: 'Desired Outcome Framework',
  aliases: ['DOF', 'Desired Outcome Model', 'Desired Outcome Brief', 'DOB'],
  
  // ===== TYPE CLASSIFICATION =====
  frameworkType: 'clarity-alignment',
  
  // ===== MULTI-RESOLUTION CONTENT =====
  content: {
    phrase: 'How will you know when you have what you want?',
    
    sentence: 'The Desired Outcome Framework helps a specific person state what they want, why it matters, and how they\'ll know they have it—using present-tense, positive, sensory evidence—so teams can align, decide, and act without endless rework.',
    
    paragraph: 'Most requests fail because they\'re vague, overloaded with audiences, or framed as "not this." People hallucinate different pictures of success, leading to the "rock fetch" cycle—delivering the wrong thing repeatedly. The Desired Outcome Framework is a deceptively simple set of questions that anchors any initiative in one clear Who, names what\'s wanted in positive language, clarifies the deeper benefit (including emotional benefits), defines evidence of success across a sufficiency→target→ideal range, surfaces obstacles and unintended negatives, and only then drafts steps. The result is a one-page decision artifact that closes the gap between request and delivery.',
    
    fullPage: `# Desired Outcome Framework

## The Core Question

**"How will you know when you have what you want?"**

## Overview

People rarely get what they want because they don\'t state it precisely or in the right order. The Desired Outcome Framework is a practical way to name the win, for one specific **Who**, in **present-tense, positive, sensory** terms—so we can align, decide, and act.

## The Rock Fetch Problem

Imagine an executive asks for something. You go off and do a lot of work. You come back. It\'s the wrong thing. They ask again with more specificity. You go off again. Come back. Still not quite right. This cycle—the "rock fetch"—happens because:

1. **People hallucinate what success means** - When someone says "tree," one person thinks apple tree, another thinks redwood
2. **The request wasn\'t well-formed** - Vague, negative, or overloaded with multiple audiences
3. **The receiver didn\'t ask clarifying questions** - Due to false urgency, wanting to appear competent, or lack of psychological safety

### The Personal Rock Fetch Story

As a kid passionate about recording technology, I told my dad I wanted a "real recorder" for my birthday. In my head, I saw a professional Tascam reel-to-reel system (thousands of dollars). In my dad\'s head, he saw an affordable Concord "sound camera" ($100). When I opened the gift, I was devastated. Had I shared the picture in my head, we could have had a conversation about the delta—and maybe found a middle ground or at least managed expectations.

## The Seven-Question Sequence

### Question Zero (Implicit): Who REALLY wants it?

Before Question 1, ask yourself: Is this what I want, or what someone else wants? Sometimes another person\'s needs are hidden underneath the request and need to be unearthed first.

### 1. Who wants it?

**Intent**: Choose exactly ONE persona or tightly logical group.

**Why it matters**: Overloading Who is the #1 failure mode. If you need two, you need two separate briefs.

### 2. What\'s wanted?

**Intent**: A single-sentence, positive statement from the Who\'s point of view.

**Why it matters**: State what you DO want, not what you don\'t want. "I want a full balloon" not "I don\'t want my balloon to pop."

### 3. What does it get them?

**Intent**: Name the deeper benefit or meta-outcome—the "why" that matters more than the want itself.

**Why it matters**: This often reveals emotional benefits (confidence, peace of mind) that are more important than the stated want. Keep asking "So that...?" until the answer stops changing decisions.

### 4. Evidence of success?

**Intent**: Present-tense, positive, SENSORY evidence across three levels.

**The Range**:
- **Barely Sufficient**: Good enough at time of delivery (not too low!)
- **Targeted Success**: What we aim to hit (ambitious yet plausible)
- **Hopelessly Idealistic**: Stretch beyond current constraints (doesn\'t need to be possible today)

**Why it matters**: This is where you prevent hallucinations. What will you SEE, HEAR, READ, TOUCH, FEEL when you have it?

### 5. Obstacles to success

**Intent**: What prevents it now? Be specific.

**Categories**: People, Process, Technology, Policy, Budget

### 6. Unintended consequences (Ecology Check)

**Intent**: What could break? Who loses? What risks compound over time?

**Why it matters**: Better to identify risks early and plan mitigations than to discover them after launch.

### 7. Steps to get there

**Intent**: Sequence with dependencies and threshold checks.

**Look for Step Zero**: Ask "What needs to happen before that?" repeatedly to uncover hidden dependencies.

## Managing Expectations

Expectations = Demands + Desires (Needs + Wants)

The gap between what someone demands and what they desire is often huge. The Desired Outcome Framework helps you ferret out both.

## Time as a Sliding Window

When discussing timing, clarify:
- **Soonest Needed**: When do you actually need to START working with it?
- **Last Responsible Moment**: Latest possible delivery without causing problems?

People often confuse these. They ask for "Friday at 4:59pm" but really need "Tuesday morning." Pressure test the timeline.

## Why People Don\'t Clarify (And Why You Should Anyway)

Common reasons people skip clarification:
1. **False sense of urgency** - "They need it now, no time to ask!"
2. **Want to be liked/accepted** - Don\'t want to seem difficult
3. **Want to appear smart/competent** - Don\'t want to admit confusion
4. **Lack of psychological safety** - Fear of being ridiculed or dismissed
5. **Comfortable in their hallucination** - Assume they know what\'s wanted

**The cost**: The rock fetch cycle. Endless rework. Missed deadlines. Damaged relationships.

## The Output

This sequence produces a compact "Desired Outcome Brief" (DOB) used to:
- Align teams before work begins
- Guide tradeoff decisions during execution
- Inform Simple Strategy and planning
- Convert into acceptance criteria (in DEGAP: Arrange)
- Set threshold-based review gates

## Why It Matters

**Two fundamental problems:**
1. Few people are really clear about what they want
2. When things don\'t go as desired, it\'s often because:
   - The request was not well formed
   - The person receiving the request wasn\'t sure what questions to ask for clarification

The Desired Outcome Framework solves both by providing a structured way to articulate wants and by giving recipients a checklist of clarifying questions to ask.`
  },
  
  // ===== RELEVANCE FRAMEWORK =====
  relevance: {
    what: 'A seven-question thinking framework that helps one specific Who articulate what they want, why it matters, and how they\'ll know success—using present-tense, positive, sensory evidence. It answers: "How will you know when you have what you want?"',
    
    why: 'Most initiatives fail or require significant rework because the desired outcome wasn\'t clearly defined upfront. People make requests that are vague, overloaded with multiple audiences, or stated in negative terms ("I don\'t want X"). Worse, people hallucinate different pictures of success, leading to the "rock fetch" cycle of repeated delivery and rework. This framework creates clarity and alignment by forcing precision before planning.',
    
    how: 'By answering seven questions in sequence: (0. Who REALLY wants it?) (1) Who wants it? (2) What\'s wanted? (3) What does it get them? (4) Evidence of success? (5) Obstacles? (6) Unintended consequences? (7) Steps to get there? The framework uses the State Classification Model to define success on a continuum from Barely Sufficient to Hopelessly Idealistic, enabling nuanced tradeoff decisions and preventing hallucination mismatches.'
  },
  
  // ===== FRAMEWORK STRUCTURE =====
  structure: {
    questions: [
      {
        number: 0,
        question: 'Who REALLY wants it? (Question Zero)',
        intent: 'Uncover whether this is your want or someone else\'s need hiding underneath.',
        guidance: 'Before diving in, check: Is this what I want, or what someone else wants? Sometimes another person\'s needs are hidden underneath the stated request. Surface this first or you\'ll solve the wrong problem.',
        examples: [
          '✓ "I thought I wanted a new job, but really my manager wants me to change roles"',
          '✓ "This is actually my boss\'s request, not mine—let me clarify with them first"',
          '✗ Assuming the stated want is the real want without checking'
        ]
      },
      {
        number: 1,
        question: 'Who wants it?',
        intent: 'Choose exactly one persona or logically related set. One only.',
        guidance: 'The Who needs to be a tight, logically related set of people/personas with roughly the same overall need or want. If multiple Whos matter, create separate Desired Outcome Briefs and later reconcile. This is the #1 failure mode—overloading Who makes everything vague.',
        examples: [
          '✓ Head of Product (and direct leadership team)',
          '✓ End users of mobile app (consumer persona)',
          '✗ "Everyone" or "The company"',
          '✗ "Sales team AND customers" (two different briefs needed)'
        ]
      },
      {
        number: 2,
        question: 'What\'s wanted?',
        intent: 'A single-sentence, positive statement from the Who\'s point of view (first person if applicable).',
        guidance: 'State what\'s wanted in positive terms, not negative language. For instance, "I want a fantastic career" and not "I don\'t want my current gig." Maintain the persona\'s point of view. If you are speaking for yourself, state it in first person. Avoid long paragraphs or multiple wants—one sentence, one want.',
        examples: [
          '✓ "I want a fast, 30-minute Priorities Review each Monday that ends with a committed, conflict-free top-5 list we publish to the org."',
          '✓ "A full balloon" (not "I don\'t want my balloon to pop")',
          '✓ "I want a professional-grade recorder for audio projects"',
          '✗ "Not have the current problem"',
          '✗ Long paragraph with multiple wants'
        ]
      },
      {
        number: 3,
        question: 'What does it get them?',
        intent: 'Name the deeper need/benefit/meta-outcome—the "why" that matters more than the want itself.',
        guidance: 'Ask "So that...?" repeatedly until it stops changing the decision. Be generous and abstract enough with the description. Include EMOTIONAL benefits where applicable—confidence, peace of mind, sense of control, reduced anxiety. The emotional benefit is often more important than the functional benefit.',
        examples: [
          '✓ "Confidence in tradeoffs, less thrash, and visible congruence between intent and execution."',
          '✓ "Peace of mind knowing my family is financially secure"',
          '✓ "Pride in creating professional-quality recordings"',
          '✗ "To have the thing" (not deep enough)',
          '✗ Just restating what\'s wanted in different words'
        ]
      },
      {
        number: 4,
        question: 'Evidence of success?',
        intent: 'Present-tense, positive, sensory evidence (see/hear/feel/read/touch). Define the range across three levels.',
        guidance: 'Describe in SENSORY terms only—what would be seen, heard, read, touched, felt. Use PRESENT TENSE. Avoid passive voice. Define three points on the continuum: **Barely Sufficient**: Truly good enough for now (at time of delivery—don\'t set too low!), **Targeted Success**: What we aim to hit (ambitious yet plausible), **Hopelessly Idealistic**: Stretch beyond current constraints—doesn\'t need to be possible with current tech/people/process. This prevents hallucination—you\'re creating a shared picture.',
        examples: [
          '✓ "Meeting ends at ≤30 min; a dated top-5 list is posted in the #product-updates channel; owners listed"',
          '✓ "I see my bank balance above $X; I hear my advisor confirm the portfolio mix"',
          '✓ "I see crisp audio waveforms on the display; I hear playback with zero distortion"',
          '✗ "We will be successful" (not sensory)',
          '✗ "The system should work well" (passive voice, not sensory)'
        ]
      },
      {
        number: 5,
        question: 'Obstacles to success',
        intent: 'What prevents it now? Missing capabilities, blockers, friction, constraints.',
        guidance: 'Categorize obstacles into People, Process, Technology, Policy, Budget, or Other. Be specific about what\'s missing or blocking progress today. Don\'t confuse obstacles with steps to overcome them—that comes in Question 7.',
        examples: [
          '✓ "Conflicting calendars; no single source of truth; unclear decision rights; tool sprawl"',
          '✓ "Budget not approved; compliance review required; legacy system integration needed"',
          '✓ "Professional recorder costs $3,000+; current budget is $100"',
          '✗ "It\'s hard" (too vague)',
          '✗ Mixing obstacles with steps to solve them'
        ]
      },
      {
        number: 6,
        question: 'Unintended negative consequences (Ecology Check)',
        intent: 'What could break? Who loses? What risks accumulate over time? What valuable thing could be put at risk?',
        guidance: 'Think deeply about potential negative effects across stakeholders, systems, and time. Include mitigations. Don\'t skip this—it\'s tempting to say "none," but there are ALWAYS tradeoffs. Think: Who loses? What breaks? What compounds over time?',
        examples: [
          '✓ "Risk of performative decisions; crowding out discovery time; over-indexing on \'top-5\' leading to neglect of operational health. Mitigations: quarterly health slot, explicit dissent capture, rotate red-team reviewer."',
          '✓ "Sales team loses flexibility; customers experience longer wait times initially"',
          '✓ "Spending $3K on recorder means no family vacation this year"',
          '✗ "None" (there are always tradeoffs)',
          '✗ Listing only minor inconveniences'
        ]
      },
      {
        number: 7,
        question: 'Steps to get to the desired outcome',
        intent: 'Sequence of actions with dependencies and threshold checks. Include key assumptions to validate first.',
        guidance: 'List steps in order. Mark thresholds (go/no-go decision points). Spell out critical dependencies and precursor steps explicitly. **Look for Step Zero**: Ask "What needs to happen BEFORE that?" repeatedly. Often there\'s a hidden step (get permission, secure budget, validate assumption) that comes first.',
        examples: [
          '✓ "0. Get budget approval (threshold: CFO sign-off). 1. Define decision rights (threshold: COO sign-off). 2. Stand up template (threshold: target evidence representable). 3. Pilot for 3 weeks..."',
          '✓ "0. Research recorder options in budget range. 1. Have conversation with dad about budget vs. ideal. 2. Find middle ground. 3. Purchase."',
          '✗ "Just do it" (no steps)',
          '✗ Missing dependencies between steps'
        ]
      }
    ]
  },
  
  // ===== RELATIONSHIPS =====
  relationships: {
    precededBy: [
      {
        frameworkId: 'gf.outcome.driver',
        description: 'Outcome Driver identifies the larger result and why it matters to the business/user. It supplies context, constraints, suggests the primary Who, surfaces the opportunity or problem, and provides early risk indicators that inform the Ecology Check.'
      }
    ],
    followedBy: [
      {
        frameworkId: 'simple-strategy',
        description: 'After the Desired Outcome is clear, Simple Strategy answers: Where/for whom will we play? How will we win? What proves we\'re winning? The Evidence range from Desired Outcome feeds directly into "What proves it?"'
      }
    ],
    partOf: [],
    relatedTo: [
      {
        frameworkId: 'state-classification-model',
        relationshipType: 'extends',
        description: 'The Evidence range (Barely Sufficient → Target → Hopelessly Idealistic) is a direct application of the State Classification Model\'s continuum concept. The thresholds between levels become go/no-go decision points.'
      },
      {
        frameworkId: 'relevance-framework',
        relationshipType: 'complements',
        description: 'The Desired Outcome Framework itself is explained using the Relevance Framework (What/Why/How). You can also use Relevance Framework to articulate any specific Desired Outcome.'
      }
    ],
    usedIn: [
      {
        frameworkId: 'pf.degap.stages',
        stage: 'Engage',
        purpose: 'Use the Desired Outcome Brief to align stakeholders on Who, What\'s Wanted, and Evidence. Capture disagreements as hypothesis gaps to resolve. The DOB becomes the alignment artifact.'
      },
      {
        frameworkId: 'pf.degap.stages',
        stage: 'Arrange',
        purpose: 'Convert Target evidence into acceptance criteria, backlog items, and Definition of Done. Schedule Threshold checks (Sufficient→Target→Ideal) as review gates. The Evidence range guides prioritization and release decisions.'
      }
    ]
  },
  
  // ===== SCOPE & APPLICABILITY =====
  scope: {
    levels: ['individual', 'team', 'organization'],
    contexts: [
      'Starting any new initiative or project',
      'Clarifying ambiguous requests (preventing rock fetch)',
      'Aligning stakeholders on outcomes',
      'Defining product requirements',
      'Strategic planning',
      'Personal goal setting',
      'Team objective setting (OKRs)',
      'Vendor/partner alignment',
      'Crafting proposals',
      'Whenever you can\'t read someone\'s mind'
    ],
    stages: [
      {
        stage: 'Before planning',
        purpose: 'Define what success looks like before allocating resources—prevent the rock fetch cycle'
      },
      {
        stage: 'During execution',
        purpose: 'Reality-check progress against evidence continuum; adjust if needed'
      },
      {
        stage: 'At review/retro',
        purpose: 'Assess what evidence was achieved and update future targets'
      }
    ]
  },
  
  // ===== PRACTICAL RESOURCES =====
  resources: {
    templates: [
      {
        id: 'blank-template',
        name: 'Desired Outcome Brief Template',
        type: 'worksheet',
        description: 'Blank one-page template with all seven questions',
        content: '# Desired Outcome Brief Template\n\nSee full template in the framework entry'
      }
    ],
    
    facilitationGuides: [
      {
        title: 'Running a Desired Outcome Session',
        duration: 75,
        roles: ['Facilitator', 'Decider', 'Scribe', 'Stakeholders (≤6 people)'],
        materials: ['Template', 'Timer', 'Parking lot', 'Evidence examples'],
        steps: [
          {
            stepNumber: 1,
            title: 'Frame',
            duration: 5,
            instructions: 'Explain why this matters: prevent rock fetch, create shared picture of success. Set ground rules: one Who, positive language, sensory evidence only.',
            tips: [
              'Share the rock fetch story or tape recorder example',
              'Emphasize that we\'ll create separate briefs if multiple Whos emerge'
            ]
          },
          {
            stepNumber: 2,
            title: 'Question Zero & Who',
            duration: 10,
            instructions: 'First check: Is this what YOU want or what someone else wants? Then decide on exactly ONE Who. Use toggle thinking—binary decision. If more than one, split into separate sessions.',
            tips: [
              'If debate arises, ask: "Whose perspective would change our answers to the other questions?"',
              'Guard this boundary strictly—overloading Who is the #1 failure mode'
            ]
          },
          {
            stepNumber: 3,
            title: 'What\'s wanted',
            duration: 10,
            instructions: 'Draft one sentence in the Who\'s voice. Read aloud. Remove any negative phrasing. Convert to positive.',
            tips: [
              'Ask: "If we said this to the Who, would they recognize it?"',
              'Test: "Can we state this in first person from the Who\'s view?"'
            ]
          },
          {
            stepNumber: 4,
            title: 'Deeper benefit',
            duration: 10,
            instructions: 'Ask "So that...?" until it stops changing the decision. Write the most abstract, emotionally resonant benefit. Include emotional benefits.',
            tips: [
              'Push past surface benefits',
              'Ask: "If they got what\'s wanted but NOT this benefit, would they still want it?"',
              'Probe for emotional benefits: confidence, peace of mind, pride, etc.'
            ]
          },
          {
            stepNumber: 5,
            title: 'Evidence range',
            duration: 25,
            instructions: 'Brainstorm sensory signals (see/hear/read/touch/feel). Sort into Sufficient/Target/Ideal. Convert everything to present tense. Ban passive voice and non-sensory language. This prevents hallucination.',
            tips: [
              '"If we had it today, what would we see/hear/read/touch/feel?"',
              'Make Sufficient truly minimal—would we ship it?',
              'Make Ideal unconstrained by today\'s tech/budget/people',
              'Target should be ambitious yet plausible',
              'Clarify time: Soonest Needed vs. Last Responsible Moment'
            ]
          },
          {
            stepNumber: 6,
            title: 'Obstacles',
            duration: 10,
            instructions: 'List what prevents this today. Categorize: People, Process, Technology, Policy, Budget.',
            tips: [
              'Focus on current blockers, not future risks (that\'s ecology)',
              'Be specific—"no tool" is better than "it\'s hard"'
            ]
          },
          {
            stepNumber: 7,
            title: 'Ecology check',
            duration: 10,
            instructions: 'Ask: Who loses? What breaks? What risks compound over time? Note mitigations. Don\'t accept "none" as an answer.',
            tips: [
              'Think in rings: immediate stakeholders, adjacent systems, long-term effects',
              'If someone says "none," push back—there are always tradeoffs',
              'Plan mitigations for identified risks'
            ]
          },
          {
            stepNumber: 8,
            title: 'Steps & thresholds',
            duration: 15,
            instructions: 'List major steps in sequence. Mark go/no-go thresholds. Spell out dependencies. LOOK FOR STEP ZERO.',
            tips: [
              'Ask "What\'s before that?" to uncover hidden precursors',
              'Thresholds should tie back to evidence levels',
              'Keep it high-level—detailed project plan comes later',
              'Step Zero is often: get permission, secure budget, validate assumption'
            ]
          },
          {
            stepNumber: 9,
            title: 'Commit & publish',
            duration: 5,
            instructions: 'Assign owners. Set review cadence. Store artifact where team can access it.',
            tips: [
              'Schedule first threshold check now',
              'Add to decision log/wiki',
              'Share with stakeholders who weren\'t in the room'
            ]
          }
        ],
        commonPitfalls: [
          'Letting the group overload Who with multiple personas',
          'Accepting vague evidence like "it works well"',
          'Skipping the ecology check or treating it as pro-forma',
          'Jumping to solutions before defining evidence',
          'Missing Step Zero',
          'Confusing Soonest Needed with Last Responsible Moment'
        ]
      }
    ],
    
    instructions: {
      individual: '# Using Desired Outcome Framework Solo\n\n1. Print or duplicate the blank template\n2. Fill in **Question Zero** first: Is this what I want or what someone else wants?\n3. Fill in **Who** (one). If unsure, pick the highest-impact Who\n4. Write **What\'s Wanted** in one sentence, first person if you are the Who\n5. Define **Evidence** across the range. Use only sensory, present-tense language. This prevents hallucination.\n6. List **Obstacles** and **Unintended Negatives** with quick mitigations\n7. Draft **Steps** with at least one clear threshold gate. Look for Step Zero.\n8. **Share** your one-pager for review; invite critique on Evidence and Ecology\n\n**Tip:** If you\'re stuck, start with Question 4 (Evidence). Sometimes it\'s easier to imagine what success looks like before articulating what you want.',
      
      team: '# Using Desired Outcome Framework with a Team\n\n**Roles needed:**\n- **Facilitator** (guides process, keeps time)\n- **Decider** (has final say on Who and Evidence)\n- **Scribe** (captures on template in real-time)\n- **Stakeholders** (≤6 people with perspective)\n\n**Run time:** 60-90 minutes\n\n**Process:** Follow the facilitation guide steps 1-9\n\n**Key success factors:**\n- Pre-read: Share template and rock fetch example beforehand\n- Time-box strictly: Use a visible timer\n- Defer tangents: Keep a parking lot for off-topic items\n- Protect the Who: Don\'t let it balloon to multiple personas\n- Make it visual: Use a large screen or whiteboard to show the template filling in\n- Create shared picture: The evidence range prevents hallucination\n\n**After the session:**\n- Type up the Brief within 24 hours\n- Circulate for review\n- Set calendar reminder for first threshold check\n- Add to your decision/project artifact repository\n\n**When NOT to use:**\n- Don\'t interrupt someone\'s explanation or creative flow\n- Don\'t be annoying with it—use gracefully over time'
    },
    
    criticalCallouts: [
      {
        id: 'core-question',
        type: 'tip',
        title: 'The Core Question',
        content: 'At its heart, the Desired Outcome Framework answers one question: "How will you know when you have what you want?" Everything else flows from this.',
        icon: '💡'
      },
      {
        id: 'rock-fetch',
        type: 'warning',
        title: 'The Rock Fetch Cycle',
        content: 'Without clarity upfront, you enter the rock fetch: deliver, wrong thing, clarify, deliver again, still not right, repeat. This wastes time, damages relationships, and kills momentum. The Desired Outcome Framework prevents this.',
        icon: '🪨'
      },
      {
        id: 'hallucination',
        type: 'warning',
        title: 'People Hallucinate Success',
        content: 'When someone says "tree," one person thinks apple tree, another thinks redwood. When someone says "recorder," one thinks $100 Concord, another thinks $3,000 Tascam. Evidence in sensory terms prevents hallucination.',
        icon: '👁️'
      },
      {
        id: 'question-zero',
        type: 'requirement',
        title: 'Question Zero: Who REALLY Wants It?',
        content: 'Before asking "Who wants it?" ask "Is this what I want or what someone else wants?" Sometimes another person\'s needs are hidden underneath. Surface this first.',
        icon: '0️⃣'
      },
      {
        id: 'one-who-rule',
        type: 'requirement',
        title: 'The One-Who Rule',
        content: 'Name exactly ONE Who. If you need two, you need two separate briefs. This is non-negotiable. Overloading Who is the #1 failure mode.',
        linkedConcept: 'state-classification-model',
        icon: '🎯'
      },
      {
        id: 'positive-language',
        type: 'requirement',
        title: 'State in Positive Terms',
        content: 'What\'s wanted must be expressed as what you DO want, not what you don\'t want. "I want a full balloon" not "I don\'t want my balloon to pop."',
        icon: '✅'
      },
      {
        id: 'emotional-benefits',
        type: 'tip',
        title: 'Include Emotional Benefits',
        content: 'When asking "What does it get them?" don\'t stop at functional benefits. Probe for emotional benefits: confidence, peace of mind, pride, sense of control. These are often MORE important.',
        icon: '❤️'
      },
      {
        id: 'sensory-evidence',
        type: 'requirement',
        title: 'Sensory Evidence Only',
        content: 'Evidence must be observable: what you see, hear, read, touch, feel. Present tense. Active voice. "I see the dashboard showing <X>" not "The system will be good." This prevents hallucination.',
        linkedConcept: 'state-classification-model',
        icon: '👁️'
      },
      {
        id: 'sufficient-calibration',
        type: 'common-mistake',
        title: 'Barely Sufficient Must Be Truly Sufficient',
        content: 'The most common mistake is setting "Barely Sufficient" too low. It must be genuinely good enough to ship/deliver at the time you expect to achieve it. Pressure test: "Would we REALLY ship this?"',
        icon: '⚠️'
      },
      {
        id: 'ideal-stretch',
        type: 'common-mistake',
        title: 'Push Hopelessly Idealistic Further',
        content: 'Rarely do people stretch Ideal far enough. Remove constraints of current tech, budget, and people. What would be amazing even if impossible today? This reveals possibilities.',
        icon: '🚀'
      },
      {
        id: 'time-window',
        type: 'tip',
        title: 'Time as Sliding Window',
        content: 'Clarify: Soonest Needed (when do you actually need to START using it?) vs. Last Responsible Moment (latest delivery without problems?). People often confuse these. Pressure test the timeline.',
        icon: '⏱️'
      },
      {
        id: 'ecology-depth',
        type: 'warning',
        title: 'Think Deeply About Ecology',
        content: 'Don\'t skip or rush the ecology check. Think across stakeholders (who loses?), systems (what breaks?), and time (what compounds?). Surface risks early. Don\'t accept "none" as an answer—there are ALWAYS tradeoffs.',
        icon: '🌊'
      },
      {
        id: 'step-zero',
        type: 'tip',
        title: 'Look for Step Zero',
        content: 'Ask "What needs to happen BEFORE that?" repeatedly. Often there\'s a hidden step: get permission, secure budget, validate assumption. Step Zero is the most important first step.',
        icon: '0️⃣'
      },
      {
        id: 'why-people-dont-clarify',
        type: 'tip',
        title: 'Why People Don\'t Clarify',
        content: 'Common reasons: false urgency, want to be liked, want to appear competent, lack of psychological safety, comfortable in their hallucination. Do it anyway—the cost of not clarifying is the rock fetch cycle.',
        icon: '🤔'
      },
      {
        id: 'when-not-to-use',
        type: 'warning',
        title: 'When NOT to Use',
        content: 'Don\'t interrupt someone\'s explanation or creative flow. Don\'t be annoying. Over time, you\'ll get graceful and people won\'t even notice you\'re doing it.',
        icon: '🚫'
      }
    ],
    
    media: []
  },
  
  // ===== LEARNING INTEGRATION =====
  learning: {
    frameworkType: 'clarity-alignment',
    difficulty: 2,
    timeToLearn: 30,
    prerequisites: [
      {
        frameworkId: 'relevance-framework',
        reason: 'Understanding What/Why/How structure helps frame any Desired Outcome'
      },
      {
        frameworkId: 'state-classification-model',
        reason: 'The Evidence continuum (Sufficient→Target→Ideal) is a direct application of state thinking with thresholds'
      }
    ],
    practiceExercises: [
      {
        title: 'Personal Goal Exercise',
        description: 'Apply the framework to a personal goal (career, health, or relationship). Practice all 7 questions.',
        duration: 20,
        difficulty: 1
      },
      {
        title: 'Rock Fetch Role Play',
        description: 'One person makes a vague request. Other person uses DOF to clarify. See how quickly you prevent the rock fetch.',
        duration: 15,
        difficulty: 2
      },
      {
        title: 'Email Request Transformation',
        description: 'Take a recent ambiguous email request you received. Rewrite it as a complete Desired Outcome Brief.',
        duration: 25,
        difficulty: 2
      }
    ]
  },
  
  // ===== VISUAL LANGUAGE =====
  visual: {
    primaryDiagram: {
      type: 'continuum',
      title: 'Evidence Range: Preventing Hallucination',
      description: 'A horizontal continuum showing three labeled states: Barely Sufficient, Targeted Success, and Hopelessly Idealistic. This prevents people from hallucinating different pictures of success.',
      svgComponent: 'EvidenceContinuumDiagram',
      interactive: true,
      altText: 'Horizontal arrow from left to right with three sections: Barely Sufficient (red), Targeted Success (blue center), Hopelessly Idealistic (green right)'
    },
    supportingDiagrams: [
      {
        type: 'flow',
        title: 'The Rock Fetch Cycle',
        description: 'Shows the cycle: Vague Request → Hallucinate Success → Deliver Rock → Wrong Thing → Clarify → Repeat. The Desired Outcome Framework breaks this cycle.',
        svgComponent: 'RockFetchCycleDiagram',
        interactive: false,
        altText: 'Circular diagram showing request-deliver-wrong-clarify cycle with a break point labeled "Use DOF"'
      }
    ],
    icon: '🎯',
    color: '#8cb299',  // sage-500
    metaphor: {
      family: 'navigation',
      phrase: 'Creating a shared map before the expedition',
      visualCues: ['🎯 Target', '🗺️ Shared Map', '🧭 Compass', '📍 Evidence Waypoints', '🪨 Avoid the Rock']
    },
    onBrandElements: {
      backgroundColor: '#f4f7f5',  // sage-50
      accentColor: '#8cb299',      // sage-500
      borderStyle: '2px solid #bad1c2',  // sage-300
      shadowStyle: '0 4px 6px rgba(140, 178, 153, 0.1)'
    }
  },
  
  // ===== QUALITY & MISSTEPS =====
  quality: {
    checklist: {
      items: [
        {
          criterion: 'Checked Question Zero: Is this what I want or what someone else wants?',
          passFail: true,
          guidance: 'Did you surface any hidden needs underneath the request?'
        },
        {
          criterion: 'One and only one Who',
          passFail: true,
          guidance: 'Can you name the Who in one breath without using "and"?'
        },
        {
          criterion: 'What\'s Wanted is a single positive sentence',
          passFail: true,
          guidance: 'No negatives ("don\'t want"). In the Who\'s voice.'
        },
        {
          criterion: 'Deeper benefit includes emotional benefits',
          passFail: true,
          guidance: 'Did you capture confidence, peace of mind, pride, or other emotional benefits?'
        },
        {
          criterion: 'Evidence uses present-tense, sensory language',
          passFail: true,
          guidance: 'Can you point to what you\'d see, hear, read, touch, feel? This prevents hallucination.'
        },
        {
          criterion: 'Three evidence marks defined (Sufficient/Target/Ideal)',
          passFail: true,
          guidance: 'Each level must be distinct and specific'
        },
        {
          criterion: 'Sufficient is truly good enough',
          passFail: true,
          guidance: 'Would you ship/deliver at this level? Pressure test it.'
        },
        {
          criterion: 'Ideal stretches beyond today',
          passFail: true,
          guidance: 'Did you remove constraints of current tech/budget/people?'
        },
        {
          criterion: 'Time clarified: Soonest Needed vs. Last Responsible Moment',
          passFail: true,
          guidance: 'Did you pressure test the timeline?'
        },
        {
          criterion: 'Obstacles categorized',
          passFail: true,
          guidance: 'People/Process/Tech/Policy/Budget identified'
        },
        {
          criterion: 'Ecology lists real negatives with mitigations',
          passFail: true,
          guidance: 'Who loses? What breaks? What compounds over time? Did you avoid "none"?'
        },
        {
          criterion: 'Steps include Step Zero and dependencies',
          passFail: true,
          guidance: 'What must happen BEFORE the first step? Where are go/no-go thresholds?'
        }
      ]
    },
    commonMissteps: {
      missteps: [
        {
          mistake: 'Skipping Question Zero',
          consequence: 'Solve for the wrong person\'s need; waste effort on something nobody actually wants',
          fix: 'Always ask first: Is this what I want or what someone else wants?',
          relatedQuestion: 'Question 0: Who REALLY wants it?'
        },
        {
          mistake: 'Overloading Who with multiple personas',
          consequence: 'The brief becomes vague and tries to satisfy everyone, satisfying no one. Enters rock fetch cycle.',
          fix: 'Create separate briefs for each distinct Who. Toggle, don\'t blend.',
          relatedQuestion: 'Question 1: Who wants it?'
        },
        {
          mistake: 'Using negative language ("don\'t want X")',
          consequence: 'Focuses on avoidance rather than achievement; harder to measure success; creates hallucination',
          fix: 'Rewrite in positive terms: what DO you want instead? "Full balloon" not "no popping"',
          relatedQuestion: 'Question 2: What\'s wanted?'
        },
        {
          mistake: 'Stopping too early on deeper benefit',
          consequence: 'Miss the real underlying need (often emotional); solve for symptoms not root cause',
          fix: 'Keep asking "So that...?" until the answer stops changing your decisions. Probe for emotional benefits.',
          relatedQuestion: 'Question 3: What does it get them?'
        },
        {
          mistake: 'Non-sensory evidence ("it will be good")',
          consequence: 'People hallucinate different pictures; impossible to objectively assess success; endless debate',
          fix: 'Convert to sensory: "I see/hear/read/touch/feel exactly what?" Use present tense, active voice.',
          relatedQuestion: 'Question 4: Evidence of success'
        },
        {
          mistake: 'Setting Barely Sufficient too low',
          consequence: 'Creates false sense of success; actual delivery won\'t meet needs; enters rock fetch',
          fix: 'Calibrate to what would truly be good enough at expected delivery time. Pressure test: "Would we REALLY ship this?"',
          relatedQuestion: 'Question 4: Evidence of success'
        },
        {
          mistake: 'Under-reaching Hopelessly Idealistic',
          consequence: 'Limits creative thinking; miss opportunities for breakthrough; constrained by today\'s limits',
          fix: 'Remove ALL current constraints. What would be amazing even if impossible today? Push further.',
          relatedQuestion: 'Question 4: Evidence of success'
        },
        {
          mistake: 'Confusing Soonest Needed with Last Responsible Moment',
          consequence: 'Deliver too late or rush unnecessarily; misalign on urgency',
          fix: 'Clarify both explicitly. Pressure test: "Are you going to use it immediately or do you need thinking time?"',
          relatedQuestion: 'Question 4: Evidence of success (timing)'
        },
        {
          mistake: 'Rushing or skipping Ecology Check',
          consequence: 'Unintended consequences surface late; expensive to fix; damages trust and relationships',
          fix: 'Dedicate real time. Think across stakeholders, systems, and time horizons. Don\'t accept "none."',
          relatedQuestion: 'Question 6: Unintended consequences'
        },
        {
          mistake: 'Missing Step Zero',
          consequence: 'Steps fail because preconditions weren\'t met (no permission, no budget); schedule slips',
          fix: 'Ask "What\'s before that?" for each step. Uncover hidden dependencies: permissions, budget, validations.',
          relatedQuestion: 'Question 7: Steps'
        },
        {
          mistake: 'No threshold gates defined',
          consequence: 'Momentum carries past decision points; waste resources on failing paths',
          fix: 'Mark clear go/no-go decision points tied to evidence levels (Sufficient→Target→Ideal)',
          relatedQuestion: 'Question 7: Steps'
        },
        {
          mistake: 'Not clarifying due to false urgency or fear',
          consequence: 'Enter the rock fetch cycle; deliver wrong thing repeatedly; waste everyone\'s time',
          fix: 'Recognize the reasons you avoid clarifying (urgency, want to appear smart, lack of safety). Do it anyway—gracefully.',
          relatedQuestion: 'All questions'
        }
      ]
    }
  },
  
  // ===== METADATA =====
  metadata: {
    createdDate: '1970-01-01',
    lastUpdated: '2025-10-01',
    version: '2.0',
    status: 'published',
    contributors: ['Richard Bandler', 'John Grinder', 'Peak Priorities Team'],
    tags: [
      'clarity',
      'alignment',
      'outcomes',
      'planning',
      'requirements',
      'decision-making',
      'type-2',
      'thinking-framework',
      'rock-fetch',
      'hallucination-prevention',
      'sensory-evidence',
      'NLP',
      'requests-and-agreements'
    ]
  }
};
